import { Percent } from '@kodiak-finance/sdk-core'
import { FeeAmount } from '@kodiak-finance/v3-sdk'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import { CurrentPriceDisplay, PriceRangeDisplay } from 'components/PriceDisplay'
import { useTokens } from 'hooks/Tokens'
import { useProcessedPoolData } from 'hooks/useProcessedPoolData'
import useTheme from 'hooks/useTheme'
import { memo, useMemo } from 'react'
import { IslandApiResponse } from 'state/data/slice'
import { Bound } from 'state/islands/mint/actions'
import { ThemedText } from 'theme'
import { formatPrice } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/formatDollarAmt'
import { formatTickPriceForRangeDisplay, getOptimalSigFigs } from 'utils/formatTickPrice'

import * as S from '../styled'
import { IslandAPRColumn } from './APRColumn'
import { PairText, PrimaryPositionIdData, RangeLineItem, StategyBadge, StyledLink } from './styled'
import UserBalanceColumn from './UserBalanceColumn'

export enum PoolProvider {
  KODIAK = 'kodiak',
  V2 = 'v2',
  CHARM = 'charm',
}

const STRATEGY_DISPLAY_NAMES: Record<PoolProvider, string> = {
  [PoolProvider.KODIAK]: 'Island',
  [PoolProvider.V2]: 'V2',
  [PoolProvider.CHARM]: 'Charm',
}

interface IslandListItemProps {
  island: IslandApiResponse
  row: number
}

const LoadingSkeleton = () => (
  <DynamicSizedLoadingRow
    style={{ width: '100%' }}
    height="60px"
    borderRadius="0"
    borderTopLeftRadius="0"
    borderTopRightRadius="0"
    borderBottomLeftRadius="0"
    borderBottomRightRadius="0"
  >
    <div />
  </DynamicSizedLoadingRow>
)

const areEqual = (prevProps: IslandListItemProps, nextProps: IslandListItemProps) => {
  // Deep compare island props that affect rendering
  const islandPropsEqual =
    prevProps.island.id === nextProps.island.id &&
    prevProps.island.token0?.id === nextProps.island.token0?.id &&
    prevProps.island.token1?.id === nextProps.island.token1?.id &&
    prevProps.island?.feeTier === nextProps.island?.feeTier &&
    prevProps.island.lowerTick === nextProps.island.lowerTick &&
    prevProps.island.upperTick === nextProps.island.upperTick &&
    prevProps.island.tvl === nextProps.island.tvl &&
    prevProps.island.weeklyFeesEarnedUSD === nextProps.island.weeklyFeesEarnedUSD &&
    prevProps.island.apr === nextProps.island.apr &&
    prevProps.island?.balanceUSD === nextProps.island?.balanceUSD &&
    prevProps.island?.vaultBalanceUSD === nextProps.island?.vaultBalanceUSD &&
    prevProps.island?.farmBalanceUSD === nextProps.island?.farmBalanceUSD

  return islandPropsEqual && prevProps.row === nextProps.row
}

const IslandListItem = memo(function IslandListItem({ island, row }: IslandListItemProps) {
  const theme = useTheme()

  const [token0, token1] = useTokens([island?.token0?.id, island?.token1?.id])

  const processedData = useProcessedPoolData(island, token0, token1)

  const feeTierPercent = useMemo(() => {
    return new Percent(island?.feeTier || 3000, 1_000_000).toSignificant()
  }, [island?.feeTier])

  const formattedPrices = useMemo(() => {
    if (!processedData) return { lower: null, upper: null, quote: null }

    const lowerDisplay = formatTickPriceForRangeDisplay(
      processedData.priceLower,
      processedData.isLowerPriceAtLimit,
      Bound.LOWER,
      island?.feeTier
    )
    const upperDisplay = formatTickPriceForRangeDisplay(
      processedData.priceUpper,
      processedData.isUpperPriceAtLimit,
      Bound.UPPER,
      island?.feeTier
    )

    return {
      lower: lowerDisplay,
      upper: upperDisplay,
      quote: processedData.quotePrice
        ? island.feeTier === FeeAmount.LOWEST
          ? processedData.quotePrice.toSignificant(5)
          : formatPrice(processedData.quotePrice, getOptimalSigFigs(processedData.quotePrice))
        : null,
    }
  }, [processedData, island?.feeTier])

  const formattedTVL = useMemo(
    () => ({
      pool: formatDollarAmount(island?.tvl),
      farm: formatDollarAmount(island?.farm?.tvl),
    }),
    [island?.tvl, island?.farm?.tvl]
  )

  if (!processedData) {
    return (
      <tbody>
        <tr>
          <td colSpan={7} style={{ width: '100%' }}>
            <LoadingSkeleton />
          </td>
        </tr>
      </tbody>
    )
  }

  return (
    <StyledLink to={processedData.positionSummaryLink} state={{ returnTo: '/liquidity/pools' }}>
      <S.Td>
        <PrimaryPositionIdData>
          <DoubleCurrencyLogo
            currency0={processedData.currencyQuote || undefined}
            currency1={processedData.currencyBase || undefined}
            size={32}
            margin
          />
          <S.FlexCol>
            <PairText>
              {processedData.currencyBase?.symbol} - {processedData.currencyQuote?.symbol}
            </PairText>
            <S.FlexRowCenter style={{ gap: '0.2rem' }}>
              <StategyBadge>{feeTierPercent}%</StategyBadge>
              <StategyBadge>{STRATEGY_DISPLAY_NAMES[island?.provider as PoolProvider] || 'Unknown'}</StategyBadge>
            </S.FlexRowCenter>
          </S.FlexCol>
        </PrimaryPositionIdData>
      </S.Td>
      <S.Td>
        <PriceRangeDisplay
          provider={island.provider}
          currencyBase={processedData?.currencyBase}
          currencyQuote={processedData?.currencyQuote}
          priceLower={processedData?.priceLower}
          priceUpper={processedData?.priceUpper}
          formattedPriceLower={formattedPrices.lower}
          formattedPriceUpper={formattedPrices.upper}
        />
      </S.Td>
      <S.Td>
        <RangeLineItem>
          <CurrentPriceDisplay
            currencyBase={processedData?.currencyBase}
            currencyQuote={processedData?.currencyQuote}
            quotePrice={processedData?.quotePrice}
            formattedQuotePrice={formattedPrices.quote}
          />
        </RangeLineItem>
      </S.Td>
      <S.Td>
        <RangeLineItem>
          <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textPrimary}>
            {formattedTVL.pool}
          </ThemedText.DeprecatedBody>
        </RangeLineItem>
      </S.Td>
      <S.Td>
        <RangeLineItem>
          <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textPrimary}>
            {formattedTVL.farm}
          </ThemedText.DeprecatedBody>
        </RangeLineItem>
      </S.Td>
      <S.Td>
        <IslandAPRColumn island={island} />
      </S.Td>
      <S.Td>
        <UserBalanceColumn island={island} />
      </S.Td>
    </StyledLink>
  )
}, areEqual)

export default IslandListItem
